/**
 * Pure derivations for the Frameworks list and a framework's own page:
 * coverage, the regulators and jurisdictions it answers to, the health tabs,
 * sorting, per-regime requirement rows and the findings filed against its
 * documents. No React, no fetching — the routes pass in what they loaded.
 *
 * @module components/regulation/frameworks/frameworkView
 */
import type { GovernanceFramework } from "../../../state/governance";
import type { RegulatoryUpdate } from "../../../state/policies";
import { SEVERITY_ORDER, jurisdictionLabel, type FindingItem } from "../finding/findingItem";
import { frameworkRollup, type FrameworkRollup, type Health, type RegimeCoverage } from "../monitor/frameworkRollup";

/** Controls addressed out of controls held; `percent` is null when nothing is catalogued (not 0%). */
export interface Coverage {
  readonly satisfied: number;
  readonly total: number;
  readonly percent: number | null;
}

export function coverageOf(satisfied: number, total: number): Coverage {
  const clamped = Math.max(0, Math.min(satisfied, total));
  return {
    satisfied: clamped,
    total,
    percent: total > 0 ? Math.round((clamped / total) * 100) : null,
  };
}

/** Distinct, non-empty values in first-seen order. */
const distinct = (values: ReadonlyArray<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) seen.add(trimmed);
  }
  return [...seen];
};

/** The regulators behind a framework's regimes ("GFSC", "ICO"). */
export const frameworkRegulators = (framework: Pick<GovernanceFramework, "regimes">): string[] =>
  distinct(framework.regimes.map((regime) => regime.issuer));

/** Where its regimes apply, readable ("guernsey" → "Guernsey"). */
export const frameworkJurisdictions = (framework: Pick<GovernanceFramework, "regimes">): string[] =>
  distinct(framework.regimes.map((regime) => (regime.jurisdiction ? jurisdictionLabel(regime.jurisdiction) : null)));

/** One list row: the framework, its rollup (health, controls, publications) and its coverage. */
export interface FrameworkRow {
  readonly framework: GovernanceFramework;
  readonly rollup: FrameworkRollup;
  readonly coverage: Coverage;
  readonly regulators: ReadonlyArray<string>;
  readonly jurisdictions: ReadonlyArray<string>;
}

export function frameworkRows(
  frameworks: ReadonlyArray<GovernanceFramework>,
  coverage: ReadonlyMap<string, RegimeCoverage>,
  publications: ReadonlyArray<RegulatoryUpdate>,
  now: number,
): FrameworkRow[] {
  return frameworks.map((framework) => {
    const rollup = frameworkRollup(framework, coverage, publications, now);
    return {
      framework,
      rollup,
      coverage: coverageOf(rollup.controls.satisfied, rollup.controls.total),
      regulators: frameworkRegulators(framework),
      jurisdictions: frameworkJurisdictions(framework),
    };
  });
}

/* ------------------------------------------------------------------ */
/* The list: tabs, search, order, summary                              */
/* ------------------------------------------------------------------ */

export type HealthTab = "all" | Health;
export const HEALTH_TABS: ReadonlyArray<readonly [HealthTab, string]> = [
  ["all", "All"],
  ["off_track", "Off track"],
  ["at_risk", "At risk"],
  ["on_track", "On track"],
];

export function healthCounts(rows: ReadonlyArray<FrameworkRow>): Readonly<Record<HealthTab, number>> {
  const counts: Record<HealthTab, number> = { all: rows.length, off_track: 0, at_risk: 0, on_track: 0 };
  for (const row of rows) counts[row.rollup.health] += 1;
  return counts;
}

/** Name, domain, regimes, regulators and jurisdictions all answer a search. */
export function matchesQuery(row: FrameworkRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  const { framework } = row;
  return [
    framework.name,
    framework.domain,
    framework.ownerEmail,
    ...framework.regimes.map((regime) => regime.name),
    ...row.regulators,
    ...row.jurisdictions,
  ].some((field) => field?.toLowerCase().includes(needle));
}

export type FrameworkOrder = "health" | "coverage" | "findings" | "name";
export const FRAMEWORK_ORDERS: ReadonlyArray<readonly [FrameworkOrder, string]> = [
  ["health", "Health"],
  ["coverage", "Coverage"],
  ["findings", "Open findings"],
  ["name", "Name"],
];

const HEALTH_RANK: Readonly<Record<Health, number>> = { off_track: 0, at_risk: 1, on_track: 2 };
/** Least covered first; nothing catalogued sorts after everything measured. */
const coverageRank = (row: FrameworkRow) => row.coverage.percent ?? 101;
const byName = (a: FrameworkRow, b: FrameworkRow) =>
  a.framework.name.localeCompare(b.framework.name, undefined, { sensitivity: "base" });

/** Worst first for every order but name, ties broken by name, so the order is stable. */
export function sortFrameworkRows(rows: ReadonlyArray<FrameworkRow>, order: FrameworkOrder): FrameworkRow[] {
  const compare = (a: FrameworkRow, b: FrameworkRow): number => {
    switch (order) {
      case "health":
        return (
          HEALTH_RANK[a.rollup.health] - HEALTH_RANK[b.rollup.health] ||
          b.framework.openFindings - a.framework.openFindings ||
          coverageRank(a) - coverageRank(b)
        );
      case "coverage":
        return coverageRank(a) - coverageRank(b);
      case "findings":
        return b.framework.openFindings - a.framework.openFindings;
      case "name":
        return 0;
    }
  };
  return [...rows].sort((a, b) => compare(a, b) || byName(a, b));
}

export function visibleFrameworkRows(
  rows: ReadonlyArray<FrameworkRow>,
  view: { readonly tab: HealthTab; readonly query: string; readonly order: FrameworkOrder },
): FrameworkRow[] {
  return sortFrameworkRows(
    rows.filter((row) => (view.tab === "all" || row.rollup.health === view.tab) && matchesQuery(row, view.query)),
    view.order,
  );
}

export interface FrameworksSummary {
  readonly frameworks: number;
  readonly coverage: Coverage;
  readonly openFindings: number;
  readonly documents: number;
  readonly regulators: number;
}

/** The whole programme at a glance; a regime shared by two frameworks counts once. */
export function frameworksSummary(
  frameworks: ReadonlyArray<GovernanceFramework>,
  coverage: ReadonlyMap<string, RegimeCoverage>,
  /**
   * The API's programme total, each regime once. Without it (an older API)
   * the frameworks' own counts are summed, which overcounts a shared regime.
   */
  openFindings?: number,
): FrameworksSummary {
  const regimeIds = new Set(frameworks.flatMap((framework) => framework.regimes.map((regime) => regime.id)));
  let satisfied = 0;
  let total = 0;
  for (const id of regimeIds) {
    satisfied += coverage.get(id)?.controlsSatisfied ?? 0;
    total += coverage.get(id)?.controlTotal ?? 0;
  }
  return {
    frameworks: frameworks.length,
    coverage: coverageOf(satisfied, total),
    openFindings: openFindings ?? frameworks.reduce((sum, framework) => sum + framework.openFindings, 0),
    documents: new Set(frameworks.flatMap((framework) => framework.documents.map((document) => document.id))).size,
    regulators: distinct(frameworks.flatMap((framework) => framework.regimes.map((regime) => regime.issuer))).length,
  };
}

/* ------------------------------------------------------------------ */
/* A framework's page                                                  */
/* ------------------------------------------------------------------ */

export type RequirementStatus = "covered" | "partial" | "not_started" | "uncatalogued";
export const REQUIREMENT_STATUS_LABEL: Readonly<Record<RequirementStatus, string>> = {
  covered: "Covered",
  partial: "Partial",
  not_started: "Not started",
  uncatalogued: "No controls",
};

export const requirementStatus = (coverage: Coverage): RequirementStatus =>
  coverage.total === 0
    ? "uncatalogued"
    : coverage.satisfied >= coverage.total
      ? "covered"
      : coverage.satisfied === 0
        ? "not_started"
        : "partial";

export interface RequirementRow {
  readonly id: string;
  readonly name: string;
  readonly regulator: string | null;
  readonly jurisdiction: string | null;
  readonly coverage: Coverage;
  readonly status: RequirementStatus;
}

/** One row per regime the framework answers to, least covered first (uncatalogued last). */
export function requirementRows(
  framework: Pick<GovernanceFramework, "regimes">,
  coverage: ReadonlyMap<string, RegimeCoverage>,
): RequirementRow[] {
  const rank: Readonly<Record<RequirementStatus, number>> = { not_started: 0, partial: 1, covered: 2, uncatalogued: 3 };
  return framework.regimes
    .map((regime) => {
      const held = coverage.get(regime.id);
      const regimeCoverage = coverageOf(held?.controlsSatisfied ?? 0, held?.controlTotal ?? 0);
      return {
        id: regime.id,
        name: regime.name,
        regulator: regime.issuer?.trim() || null,
        jurisdiction: regime.jurisdiction ? jurisdictionLabel(regime.jurisdiction) : null,
        coverage: regimeCoverage,
        status: requirementStatus(regimeCoverage),
      };
    })
    .sort(
      (a, b) =>
        rank[a.status] - rank[b.status] ||
        (a.coverage.percent ?? 0) - (b.coverage.percent ?? 0) ||
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
}

const severityRank = (item: FindingItem) =>
  item.severity === null ? SEVERITY_ORDER.length : SEVERITY_ORDER.indexOf(item.severity);

/** Open findings filed against the framework's documents: most severe, then newest, first. */
export function relatedOpenFindings(
  items: ReadonlyArray<FindingItem>,
  framework: Pick<GovernanceFramework, "documents">,
): FindingItem[] {
  const documents = new Set(framework.documents.map((document) => document.id));
  return items
    .filter((item) => item.status === "open" && item.policyId !== null && documents.has(item.policyId))
    .sort((a, b) => severityRank(a) - severityRank(b) || b.raisedAt - a.raisedAt);
}

/** Open findings per document id. */
export function openFindingsByDocument(items: ReadonlyArray<FindingItem>): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (item.status !== "open" || item.policyId === null) continue;
    counts.set(item.policyId, (counts.get(item.policyId) ?? 0) + 1);
  }
  return counts;
}

/** Publications still owed a word: unreviewed, or proposed relevant with nobody's decision. */
export const decisionsOwed = (publications: ReadonlyArray<RegulatoryUpdate>): number =>
  publications.filter(
    (update) =>
      (update.applicability ?? "needs_review") === "needs_review" ||
      (update.applicability === "relevant" && update.applicabilityDecidedByEmail == null),
  ).length;

/** Newest first, by publication date (detection when unpublished). */
export const newestFirst = (publications: ReadonlyArray<RegulatoryUpdate>): RegulatoryUpdate[] =>
  [...publications].sort(
    (a, b) => Date.parse(b.publishedAt ?? b.detectedAt) - Date.parse(a.publishedAt ?? a.detectedAt),
  );
